import React, { useMemo, useState } from 'react'
import PublicationCard from '../utils/PublicationCard'
import PageHero from '../utils/PageHero'
import allPublications from '../data/publications.json'

// The Publications page lists work from 2023 onward; earlier papers stay in
// the data file but are not shown.
const publications = allPublications.filter((p) => p.year >= 2023)

const categoryOf = (type = '') => {
  const t = type.toLowerCase()
  if (t.includes('journal')) return 'Journal'
  if (t.includes('workshop')) return 'Workshop'
  if (t.includes('preprint')) return 'Preprint'
  if (t.includes('conference')) return 'Conference'
  return 'Other'
}

const CATEGORIES = ['All', 'Conference', 'Journal', 'Workshop', 'Preprint', 'Other']

const PublicationScreen = () => {
  const [category, setCategory] = useState('All')
  const [query, setQuery] = useState('')

  const counts = useMemo(() => {
    const c = { All: publications.length }
    publications.forEach((p) => {
      const k = categoryOf(p.type)
      c[k] = (c[k] || 0) + 1
    })
    return c
  }, [])

  const byYear = useMemo(() => {
    const q = query.trim().toLowerCase()
    const filtered = publications.filter((p) => {
      if (category !== 'All' && categoryOf(p.type) !== category) return false
      if (!q) return true
      return [p.title, p.confTitle, p.location, ...(p.authors || [])]
        .join(' ')
        .toLowerCase()
        .includes(q)
    })
    const groups = {}
    filtered.forEach((p) => {
      ;(groups[p.year] = groups[p.year] || []).push(p)
    })
    return Object.keys(groups)
      .sort((a, b) => b - a)
      .map((year) => ({ year, items: groups[year] }))
  }, [category, query])

  const years = [...new Set(publications.map((p) => p.year))]
  const venues2026 = publications.filter((p) => p.year >= 2026).length

  return (
    <div>
      <PageHero
        eyebrow='Research Output'
        title='Publications'
        description='Peer-reviewed papers from the SPEED Lab across top venues in machine learning, computer vision, NLP and security.'
      >
        <div className='page-hero__stats'>
          <div>
            <strong>{publications.length}</strong>
            <span>Publications</span>
          </div>
          <div>
            <strong>{venues2026}</strong>
            <span>Papers in 2026</span>
          </div>
          <div>
            <strong>{years.length}</strong>
            <span>Years of research</span>
          </div>
        </div>
      </PageHero>

      <div className='container-wide page-body' style={{ paddingTop: 0 }}>
        <div className='pub-toolbar'>
          <div className='chip-group'>
            {CATEGORIES.filter((c) => counts[c]).map((c) => (
              <button
                key={c}
                className={`chip ${category === c ? 'is-active' : ''}`}
                onClick={() => setCategory(c)}
              >
                {c}
                <span>{counts[c]}</span>
              </button>
            ))}
          </div>
          <label className='search'>
            <i className='fa-solid fa-magnifying-glass' />
            <input
              type='search'
              placeholder='Search title, author, venue'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>
        </div>

        {byYear.length === 0 && (
          <div className='empty-state'>No publications match your search.</div>
        )}

        {byYear.map(({ year, items }) => (
          <section className='pub-year' key={year}>
            <div className='pub-year__label'>{year}</div>
            <div className='pub-list'>
              {items.map((record) => (
                <PublicationCard key={record.id} {...record} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  )
}

export default PublicationScreen
